/// <amd-module name="Search/Code/Flux/Sources/TelemetrySpy" />

import * as BaseActionsHub from "Search/Shared/Base/ActionsHub";
import { ActionsHub } from "Search/Code/Flux/ActionsHub";
import { StoresHub } from "Search/Code/Flux/StoresHub";
import { CodeResult, CodeQueryResponse, SearchQuery } from "Search/LegacyWebApi/Contracts/Code";
import { TelemetryWriter } from "Search/Code/Flux/Sources/TelemetryWriter";
import { CustomerIntelligenceConstants } from "Search/Code/Constants";

/**
 * Writes telemetry events based on actions in code search page.
 */
export class TelemetrySpy {
    constructor(
        private telemetryWriter: TelemetryWriter,
        private actionsHub: ActionsHub,
        private storesHub: StoresHub) {
        this.registerActionsHubHandlers();
    }

    public dispose = (): void => {
        this.disposeActionsHubHandlers();
        this.telemetryWriter.dispose();
    }

    private registerActionsHubHandlers = (): void => {
        this.actionsHub.searchStarted.addListener(this.publishSearchStarted);
        this.actionsHub.pageInitializationStarted.addListener(this.publishPageInitializationStarted);
        this.actionsHub.pageInitializedWithSeedData.addListener(this.publishLaunchPointTelemetry);
        this.actionsHub.resultsLoaded.addListener(this.publishResultsLoaded);
        this.actionsHub.itemChanged.addListener(this.publishItemChanged);
        this.actionsHub.previewOrientationChanged.addListener(this.publishPreviewOrientationChanged);
        this.actionsHub.sortOptionChanged.addListener(this.publishSortOptionChanged);
    }

    private disposeActionsHubHandlers = (): void => {
        this.actionsHub.searchStarted.removeListener(this.publishSearchStarted);
        this.actionsHub.pageInitializationStarted.removeListener(this.publishPageInitializationStarted);
        this.actionsHub.pageInitializedWithSeedData.removeListener(this.publishLaunchPointTelemetry);
        this.actionsHub.resultsLoaded.removeListener(this.publishResultsLoaded);
        this.actionsHub.itemChanged.removeListener(this.publishItemChanged);
        this.actionsHub.previewOrientationChanged.removeListener(this.publishPreviewOrientationChanged);
        this.actionsHub.sortOptionChanged.removeListener(this.publishSortOptionChanged);
    }

    private publishPageInitializationStarted = (payload: BaseActionsHub.IPageInitializationStartedPayload<SearchQuery>): void => {
        this.telemetryWriter.notifySearchStarted();
        // defer so that it doesn't block action handlers.
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SearchStarted, {});
        }, 0);
    }

    private publishLaunchPointTelemetry = (payload: BaseActionsHub.IPageInitializedWithSeedDataPayload<CodeQueryResponse>): void => {
        setTimeout(() => {
            const { launchPoint } = payload;
            if (launchPoint) {
                this.telemetryWriter.publish(CustomerIntelligenceConstants.LaunchPoint, { launchPoint });
            }
        }, 0);
    }

    private publishSearchStarted = (payload: BaseActionsHub.ISearchStartedPayload<SearchQuery>): void => {
        if (!payload.fetchMoreScenario) {
            this.telemetryWriter.notifySearchStarted();
        }

        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SearchStarted, {});
            if (payload.fetchMoreScenario) {
                this.telemetryWriter.publish(CustomerIntelligenceConstants.FetchMoreResultsStarted);
            }
        }, 0);
    }

    private publishResultsLoaded = (payload: BaseActionsHub.IResultsLoadedPayload<CodeQueryResponse, CodeResult>): void => {
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.ResultsLoaded, {
                totalCount: payload.response.results.count,
                resultsShown: payload.response.results.values.length
            });
        }, 0);
    }

    private publishItemChanged = (payload: BaseActionsHub.IItemChangedPayload<CodeResult>): void => {
        const { searchStoreState } = this.storesHub.getAggregatedState();
        const { response } = searchStoreState;
        if (response) {
            setTimeout(() => {
                this.telemetryWriter.publish(CustomerIntelligenceConstants.ItemChanged, {
                    index: response.results.values.indexOf(payload.item)
                });
            }, 0);
        }
    }

    private publishPreviewOrientationChanged = (payload: BaseActionsHub.IPreviewOrientationChangedPayload): void => {
        setTimeout(() => {
            this.telemetryWriter.publish(CustomerIntelligenceConstants.PreviewOrientationChanged, {
                currentOrientation: payload.previewOrientation.name
            })
        }, 0);
    }

    private publishSortOptionChanged = (payload: BaseActionsHub.ISortOptionChangedPayload<CodeResult>): void => {
        setTimeout(() => {
            const { sortOption } = payload;
            this.telemetryWriter.publish(CustomerIntelligenceConstants.SortOptionChanged, { ...sortOption });
        }, 0);
    }
}
